// QrAuditPage — condo-wide scan audit (2026-09-09, Jaime live-testing round).
// Backend contract (BackendDev lane, may not be deployed yet): GET
// /api/qr-access/audit?condoId= → list of scan audit entries carrying photo,
// name, entry type, destination address, scanner identity, direction
// (entrada/salida), timestamp and result. This page codes DEFENSIVELY against
// that contract: every field is aliased across plausible key names and every
// render degrades gracefully when a field is missing ("—" placeholders,
// initial-avatar fallback, badge omitted when direction unknown).

import { useCallback, useEffect, useState } from 'react';
import { ScanLine, RefreshCw, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import AuthImage from '../components/scan/AuthImage';
import { useAuth } from '../contexts/AuthContext';
import { securityAPI } from '../services/api';

type AuditDirection = 'entrada' | 'salida';

interface AuditEntry {
  id: string;
  name: string;
  photo?: string;
  type: 'resident' | 'employee' | 'staff' | 'visitor' | 'invalid' | 'unknown';
  destination?: string;
  scannerName?: string;
  scannerRole?: string;
  direction?: AuditDirection;
  timestamp?: string;
  status: 'allowed' | 'denied' | 'expired';
  reason?: string;
  code?: string; rawCode?: string; visitorPhone?: string; accessCodeId?: string;
  visitorsLogId?: string; validFrom?: string; validUntil?: string; idPhotoUrl?: string;
  vehiclePlate?: string;
  /** Walk-in enhancement (Jaime 2026-09-16): optional ID document number. */
  visitorIdNumber?: string;
  reasonCode?: string;
  /** Code origin (roadmap item E): 'walk_in' → "Sin QR" chip. */
  origin?: string;
}

// First non-empty string among plausible backend key aliases.
function pick(row: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = row?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

const TYPE_LABEL: Record<AuditEntry['type'], string> = {
  resident: 'Residente',
  employee: 'Empleado',
  staff: 'Empleado',
  visitor: 'Visitante',
  invalid: 'Inválido',
  unknown: '—',
};

const TYPE_BADGE: Record<AuditEntry['type'], string> = {
  resident: 'bg-sky-500/20 text-sky-300',
  employee: 'bg-violet-500/20 text-violet-300',
  staff: 'bg-violet-500/20 text-violet-300',
  visitor: 'bg-[#36CCD8]/20 text-[#36CCD8]',
  invalid: 'bg-red-500/20 text-red-300',
  unknown: 'bg-gray-500/20 text-gray-300',
};

function scannerRoleLabel(role?: string): string | undefined {
  if (!role) return undefined;
  const map: Record<string, string> = {
    admin: 'Administrador',
    sub_admin: 'Sub-administrador',
    seguridad: 'Seguridad',
    security: 'Seguridad',
    super_admin: 'Super Admin',
    directive: 'Directiva',
    resident: 'Residente',
  };
  return map[role] ?? role;
}

// dd/mm hh:mm (es-VE style), matching VerifyQR history formatting.
function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

function normalizeEntry(raw: unknown, i: number): AuditEntry {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rawType = (pick(r, ['entry_type', 'card_type', 'scan_type', 'type', 'code_type']) ?? '').toLowerCase();
  let type: AuditEntry['type'] = 'unknown';
  if (rawType === 'resident') type = 'resident';
  else if (rawType === 'employee' || rawType === 'staff') type = 'employee';
  else if (rawType === 'visitor' || rawType === 'guest' || rawType === 'provider' || rawType === 'service') type = 'visitor';
  else if (rawType === 'invalid') type = 'invalid';

  const dirRaw = (pick(r, ['direction', 'scan_direction', 'dir']) ?? '').toLowerCase();
  const direction: AuditDirection | undefined =
    dirRaw === 'entrada' || dirRaw === 'in' || dirRaw === 'entry'
      ? 'entrada'
      : dirRaw === 'salida' || dirRaw === 'out' || dirRaw === 'exit'
        ? 'salida'
        : undefined;

  const valid = r.valid === true || r.allowed === true;
  const reason = pick(r, ['reason', 'invalid_reason', 'deny_reason', 'result_reason']);
  let status: AuditEntry['status'] = valid ? 'allowed' : 'denied';
  // Backend (2026-09-09 fix) sends Spanish `result`: Permitido / Denegado / Expirado.
  // English + legacy valid/status/reason aliases kept as defensive fallbacks.
  const resultRaw = (pick(r, ['result', 'outcome']) ?? '').toLowerCase();
  if (resultRaw === 'permitido' || resultRaw === 'allowed') status = 'allowed';
  else if (resultRaw === 'denegado' || resultRaw === 'denied') status = 'denied';
  else if (resultRaw === 'expirado' || resultRaw === 'expired') status = 'expired';
  else if (r.status === 'expired' || (reason && /expired|expirad/i.test(reason))) status = 'expired';
  else if (r.status === 'denied' || r.result === 'DENIED') status = 'denied';
  else if (r.status === 'allowed' || r.result === 'ALLOWED') status = 'allowed';

  return {
    id: pick(r, ['id', 'scan_id', 'audit_id']) ?? `row-${i}`,
    name: pick(r, ['name', 'full_name', 'visitor_name', 'employee_name', 'display_name', 'user_name']) ?? 'Usuario',
    photo: pick(r, ['photo', 'photo_url', 'visitor_photo', 'visitor_photo_url', 'employee_photo', 'avatar_url', 'profile_photo', 'picture']),
    type,
    destination: pick(r, ['destination', 'destination_address', 'dest_address', 'address', 'unit', 'unit_label', 'unit_address']),
    scannerName: pick(r, ['scanner_name', 'scanned_by', 'scanner', 'guard_name']),
    scannerRole: pick(r, ['scanner_role', 'scanned_by_role', 'guard_role']),
    direction,
    timestamp: pick(r, ['timestamp', 'scanned_at', 'created_at', 'scan_time', 'at']),
    status,
    reason,
    code: pick(r, ['code', 'access_code']), rawCode: pick(r, ['raw_code', 'rawCode']),
    visitorPhone: pick(r, ['visitor_phone', 'phone']), accessCodeId: pick(r, ['access_code_id']),
    visitorIdNumber: pick(r, ['visitor_id_number', 'visitorIdNumber', 'id_number', 'idNumber']),
    vehiclePlate: pick(r, ['vehicle_plate', 'vehiclePlate']),
    visitorsLogId: pick(r, ['visitors_log_id']), validFrom: pick(r, ['valid_from']), validUntil: pick(r, ['valid_until']),
    idPhotoUrl: pick(r, ['id_photo_url', 'id_document_url']), reasonCode: pick(r, ['reason_code']),
    origin: pick(r, ['origin', 'code_origin', 'codeOrigin']),
  };
}

export default function QrAuditPage() {
  const { user, activeCondoId } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Lightbox (Jaime 2026-09-12): full-screen photo zoom for expanded-row images.
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const [idPhotoSrc, setIdPhotoSrc] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Prefer the ACTIVE condo context over the stale user.condoId snapshot.
      const condoId = activeCondoId || user?.condoId;
      if (!condoId) throw new Error('No se encontró un condominio activo.');
      const res = await securityAPI.audit(condoId);
      // Backend (src/modules/qr-access/routes.js) returns:
      // { success, data: { events, count, limit, source } }. Defensive aliases
      // (entries|audit|scans) kept for older/other backends; bare array too.
      const payload = res.data?.data ?? res.data;
      const list: unknown[] =
        payload?.events ?? payload?.entries ?? payload?.audit ?? payload?.scans ??
        (Array.isArray(payload) ? payload : []);
      setEntries((Array.isArray(list) ? list : []).map(normalizeEntry));
    } catch (err: any) {
      // Explicit reasons: 403 = no perms, 404 = not deployed, else server error.
      if (err?.response?.status === 403) {
        setError('No tienes permisos para ver la auditoría de escaneos (solo administradores del condominio).');
      } else if (err?.response?.status === 404) {
        setError('La auditoría de escaneos aún no está disponible en el servidor.');
      } else {
        const msg = err?.response?.data?.error || err?.message || 'Error desconocido';
        setError(`Error de servidor: ${msg}`);
      }
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [user?.condoId]);

  useEffect(() => { load(); }, [load]);

  return (
    // Rendered inside the AppShell/LensDashboard content slot (route
    // /building/admin/qr-audit → LensDashboard case): the shell owns the
    // sidebar/header/padding, so this is a plain content block, not a page.
    <div className="max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
     
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Auditoría de Escaneos</h1>
          <p className="text-xs text-gray-500">Registro condominio · entradas y salidas</p>
        </div>
        <button onClick={load} className="p-2 text-gray-400 hover:text-white rounded-xl" title="Recargar">
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <main className="pb-2">
        {loading && (
          <div className="flex items-center justify-center gap-3 py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" /> Cargando auditoría…
          </div>
        )}
        {!loading && error && (
          <div className="rounded-2xl bg-gray-800 border border-amber-500/40 p-6 text-amber-200 text-sm">{error}</div>
        )}
        {!loading && !error && entries.length === 0 && (
          <div className="flex flex-col items-center py-16 text-gray-500">
            <ScanLine className="w-12 h-12 mb-3" />
            <p>Sin escaneos registrados todavía.</p>
          </div>
        )}
        {!loading && !error && entries.length > 0 && (
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-6">
            <div className="space-y-3">
              {entries.map((e) => (
                <div key={e.id} className="bg-gray-700 rounded-xl overflow-hidden">
                  <button type="button" className="w-full text-left flex items-start gap-3 p-3 hover:bg-gray-600/60" onClick={() => setExpandedId(expandedId === e.id ? null : e.id)} aria-expanded={expandedId === e.id}>
                  {e.photo ? (
                    <img src={e.photo} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                      onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gray-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {e.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium truncate">{e.name}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TYPE_BADGE[e.type]}`}>
                        {TYPE_LABEL[e.type]}
                      </span>
                      {/* Roadmap item E: guard-registered walk-in → "Sin QR" chip. */}
                      {e.origin === 'walk_in' && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">
                          Sin QR
                        </span>
                      )}
                      {e.direction && (
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${e.direction === 'entrada' ? 'bg-emerald-500/25 text-emerald-300' : 'bg-amber-500/25 text-amber-300'}`}>
                          {e.direction === 'entrada' ? '⬆ Entrada' : '⬇ Salida'}
                        </span>
                      )}
                    </div>
                    {e.destination && (
                      <p className="text-gray-400 text-xs">Destino: {e.destination}</p>
                    )}
                    {e.scannerName && (
                      <p className="text-gray-500 text-xs">
                        Escaneado por {e.scannerName}{scannerRoleLabel(e.scannerRole) ? ` · ${scannerRoleLabel(e.scannerRole)}` : ''}
                      </p>
                    )}
                    {e.reason && e.status !== 'allowed' && (
                      <p className="text-red-300/80 text-xs">{e.reason}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-gray-400 text-xs whitespace-nowrap">{fmtDateTime(e.timestamp)}</p>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      e.status === 'allowed' ? 'bg-emerald-500/25 text-emerald-300' : e.status === 'expired' ? 'bg-amber-500/25 text-amber-300' : 'bg-red-500/25 text-red-300'
                    }`}>
                      {e.status === 'allowed' ? 'Permitido' : e.status === 'expired' ? 'Expirado' : 'Denegado'}
                    </span>
                  </div>
                  {expandedId === e.id ? <ChevronUp className="w-4 h-4 text-gray-400 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 mt-1" />}
                  </button>
                  {expandedId === e.id && (
                    <div className="border-t border-gray-600 p-4 text-xs text-gray-300 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(e.photo || e.idPhotoUrl) && (
                        <div className="flex gap-3 sm:col-span-2">
                          {e.photo && (
                            <div>
                              <img src={e.photo} alt="Foto de perfil" className="w-24 h-24 rounded-lg object-cover bg-gray-600 cursor-zoom-in"
                                onClick={() => setZoomSrc(e.photo || null)}
                                onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                              <p className="text-gray-500 mt-1">Foto de perfil</p>
                            </div>
                          )}
                          {e.idPhotoUrl && (
                            <div>
                              <AuthImage url={e.idPhotoUrl} alt="Documento capturado" className="w-32 h-24 object-cover rounded-lg cursor-zoom-in"
                                onSrc={setIdPhotoSrc}
                                onClick={() => { if (idPhotoSrc) setZoomSrc(idPhotoSrc); }} />
                              <p className="text-gray-500 mt-1">Documento capturado</p>
                            </div>
                          )}
                        </div>
                      )}
                      {[['Código', e.code], ['raw_code', e.rawCode], ['Origen', e.origin === 'walk_in' ? 'Sin QR' : undefined], ['Dirección', e.direction], ['Resultado', e.status], ['Tipo', TYPE_LABEL[e.type]], ['Visitante/persona', e.name], ['Teléfono visitante', e.visitorPhone], ['ID visitante', e.visitorIdNumber], ['Placa', e.vehiclePlate], ['Destino/unidad', e.destination], ['Fecha/hora', e.timestamp && new Date(e.timestamp).toLocaleString('es-MX')], ['Scanner', e.scannerName], ['Rol scanner', e.scannerRole], ['Código interno', e.accessCodeId], ['Registro visita', e.visitorsLogId], ['Vigente desde', e.validFrom], ['Vigente hasta', e.validUntil], ['Motivo', e.reason], ['Código motivo', e.reasonCode]].map(([label, value]) => value ? <div key={label}><span className="text-gray-500">{label}: </span><span className="break-words">{value}</span></div> : null)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      {zoomSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Vista ampliada de foto"
          onClick={() => setZoomSrc(null)}
        >
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => setZoomSrc(null)}
            className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-gray-700/90 text-white text-2xl leading-none flex items-center justify-center border border-gray-500 shadow-lg"
          >
            ×
          </button>
          <img src={zoomSrc} alt="Foto ampliada" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(ev) => ev.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

